import { useState, useMemo, useCallback } from 'react';
import { ChevronDown, Globe } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { useModelStore } from '../../../state/store/chat/use-model-store';
import { useGetLlmModel } from '../../../hooks/use-llm-model';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from 'hooks/use-mobile';
import { useSubscriptionStore } from 'state/store/subscription';
import { PLANS } from 'constant/plan';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { MessageRole } from 'features/chat/types/chat';
import { useParams } from 'react-router-dom';
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { Box } from 'lucide-react';
import { useTheme } from 'components/core/theme-provider';
import deepseekv32 from 'assets/images/deepseekv3.2.png';
import gptoss from 'assets/images/gptoss120b.png';
import kimik from 'assets/images/kimik2.png';
import llama33 from 'assets/images/llama3.3.png';
import llama4maverick from 'assets/images/llama4maverick.png';
import qwen32 from 'assets/images/qwen32b.png';
import glm45 from 'assets/images/glm4.5.png';
import { getTranslatedModelMetadata, getRegionIcon, getRegionHostingInfo } from 'constant/model-metadata';
import { LLM_MODEL_REGIONS, type LlmModelRegion } from 'constant/llm-model';
import { cn } from 'lib/utils';







interface ModelSelectorProps {
  isDisabled?: boolean;
  /** Agent mode: show this label (the agent's model) and lock the dropdown. */
  overrideLabel?: string | null;
  overrideLoading?: boolean;
  overrideRegion?: LlmModelRegion;
}

const RegionBadge = ({
  region,
  variant = 'default',
}: {
  region?: LlmModelRegion;
  variant?: 'default' | 'selected';
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation();

  if (!region) return null;

  const isDark = theme === 'dark';
  const useWhiteIcon = isDark && variant === 'selected';
  const regionIcon = getRegionIcon(region, { useWhiteIcon });
  const isGlobal = region === LLM_MODEL_REGIONS.GLOBAL;
  const hostingLabel = getRegionHostingInfo(region, t);

  return (
    <span
      title={hostingLabel}
      aria-label={hostingLabel}
      className="inline-flex h-4 w-4 items-center justify-center flex-shrink-0"
    >
      {regionIcon ? (
        <img src={regionIcon} alt="" className="h-4 w-4 rounded-full object-cover" />
      ) : null}
      {isGlobal ? (
        <Globe
          className={cn(
            'h-4 w-4',
            useWhiteIcon ? 'text-[#ffffff]' : 'text-sky-500'
          )}
        />
      ) : null}
    </span>
  );
};

export const ModelSelector = ({
  isDisabled: externalIsDisabled,
  overrideLabel,
  overrideLoading,
  overrideRegion,
}: ModelSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);
  const [hoveredModelId, setHoveredModelId] = useState<string | null>(null);

  const selectedModel = useModelStore((state) => state.selectedModel);
  const models = useModelStore((state) => state.models);
  const openNewChatWithModel = useModelStore((state) => state.openNewChatWithModel);
  const userChangedModel = useModelStore((state) => state.userChangedModel);
  const setActiveConversationModelName = useChatStore(
    (state) => state.setActiveConversationModelName
  );

  const { conversationId } = useParams();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const { isLoading, isFetching } = useGetLlmModel();

  const shouldDisableDropdown = useChatStore((state) => {
    if (!conversationId) return false;
    const conv = state.conversations[conversationId];
    const streamingMessage = conv?.messages.find(
      (msg) => msg.recordId === conv?.streamingAssistantMessageId
    );
    return (
      streamingMessage?.role === MessageRole.ASSISTANT ||
      (conv?.activeStreamsMap?.size ?? 0) > 0 ||
      conv?.assistantResponseRemaining !== 0
    );
  });

  const isStreaming =
    externalIsDisabled ||
    (shouldDisableDropdown && !!conversationId);

  const currentPlan = useSubscriptionStore((state) => state.currentPlan);
  // No subscription service for this tenant → treat absent plan as UNLIMITED
  // so the model dropdown still renders.
  const effectivePlanName = currentPlan?.planCode ?? PLANS.UNLIMITED;

  const memoizedModels = models;
  const isModelLoading = (isLoading || isFetching) && memoizedModels.length === 0;

  const displayedModels = useMemo(
    () => memoizedModels.slice(0, visibleCount),
    [memoizedModels, visibleCount]
  );

  const displayName = useMemo(() => {
    if (overrideLoading) return '…';
    if (overrideLabel) return overrideLabel;
    if (!selectedModel?.name) return '';
    return selectedModel.name;
  }, [overrideLabel, overrideLoading, selectedModel?.name]);

  const handleModelSelect = useCallback(
    (modelId: string) => {
      if (selectedModel?.id !== modelId) {
        const model = memoizedModels.find((m) => m.id === modelId);
        if (model) {
          setActiveConversationModelName(model.id);
          openNewChatWithModel(model);
          userChangedModel(true);
        }
      }
      setOpen(false);
    },
    [
      memoizedModels,
      openNewChatWithModel,
      selectedModel?.id,
      setActiveConversationModelName,
      userChangedModel,
    ]
  );

  // 🔒 Hook declared BEFORE early return (eslint safe)
  const ensureScrollable = useCallback(
    (el: HTMLDivElement | null) => {
      if (!el) return;
      if (
        el.scrollHeight <= el.clientHeight &&
        visibleCount < memoizedModels.length
      ) {
        setVisibleCount((prev) =>
          Math.min(prev + 5, memoizedModels.length)
        );
      }
    },
    [visibleCount, memoizedModels.length]
  );

  if (effectivePlanName === PLANS.NOPLAN) {
    return null;
  }

  if (isModelLoading) {
    return (
      <div className="flex items-center">
        <Skeleton className="h-8 w-[120px] rounded-full bg-muted dark:bg-primary-foreground/20" />
      </div>
    );
  }

  if (memoizedModels.length === 0) {
    return null;
  }

  const modelIconMap: Record<string, string> = {
    'DeepSeek-V3-0324': deepseekv32,
    'Qwen3-32B': qwen32,
    'Qwen2.5-VL-72B-Instruct': qwen32,
    'Llama-3.3-70B-Instruct-fast': llama33,
    'Llama-4-Maverick': llama4maverick,
    'gpt-oss-120b': gptoss,
    'Kimi-K2-Instruct': kimik,
    'GLM-4.5': glm45,
  };

  const modelsIconViewer = (model: any) => {
    const icon = modelIconMap[model.id];
    if (icon) {
      return <img className="w-4 h-4" src={icon} alt={model.name} />;
    }
    return (
      <Box
        className={`w-4 h-4 ${selectedModel?.id === model.id ? 'text-primary dark:text-[#FFFFFF]' : ''
          }`}
      />
    );
  };

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip open={isStreaming ? undefined : false}>
        <DropdownMenu
          open={isStreaming ? false : open}
          onOpenChange={(isOpen) => {
            if (isStreaming) return;
            setOpen(isOpen);
            if (!isOpen) {
              setVisibleCount(5);
              setHoveredModelId(null);
            }
          }}
          modal={false}
        >
          <TooltipTrigger asChild>
            <div className="min-w-0 max-w-[140px] sm:max-w-[160px] md:max-w-full">
              <DropdownMenuTrigger asChild disabled={isStreaming}>
                <Button
                  variant="ghost"
                  disabled={isStreaming}
                  className={`max-w-full min-w-0 w-auto justify-between transition-colors duration-200 h-auto px-0 py-1 focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none hover:bg-transparent ${theme === 'dark' ? 'text-white' : ''} ${isStreaming ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                >
                  {/* <Box
                    className={`sm:h-4 h-3 sm:w-4 w-3 flex-shrink-0 ${theme === 'dark'
                      ? 'text-[#ffffff]'
                      : 'text-[#3D3D3D]'
                      }`}
                  /> */}
                  <span
                    className={`text-sm font-normal leading-5 overflow-hidden text-ellipsis whitespace-nowrap min-w-0 ${theme === 'dark'
                      ? 'text-[#ffffff]'
                      : 'text-[#3D3D3D]'
                      }`}
                    title={overrideLabel ?? selectedModel?.name}
                  >
                    {displayName}
                  </span>
                  <RegionBadge
                    region={overrideLabel || overrideLoading ? overrideRegion : selectedModel?.region}
                    variant="selected"
                  />
                  <ChevronDown
                    className={`sm:h-4 sm:w-4 h-3 w-3 flex-shrink-0 ${theme === 'dark'
                      ? 'text-[#ffffff]'
                      : 'text-[#3D3D3D]'
                      }`}
                  />
                </Button>
              </DropdownMenuTrigger>
            </div>
          </TooltipTrigger>

          <DropdownMenuContent
            align="start"
            className={`p-0 bg-background border-[0.5px] ${theme === 'dark' ? 'border-black-500' : 'border-border/60'
              } shadow-lg rounded-xl overflow-visible`}
          >
            <div className="relative">
              {/* Hover detail panel — floats on the left of the list with a gap */}
              {(() => {
                if (!hoveredModelId) return null;
                const focusedModel = memoizedModels.find((m) => m.id === hoveredModelId);
                if (!focusedModel) return null;
                const rawProvider = focusedModel.providerLabel;
                const meta = getTranslatedModelMetadata(focusedModel.id, t, {
                  providerLabel: rawProvider,
                  region: focusedModel.region,
                });
                const providerLabel = rawProvider ?? meta.provider;
                const hostingRegion = focusedModel.region ?? meta.region;
                const { description, hostingInfo } = meta;
                return (
                  <div
                    className={`hidden md:flex absolute top-0 right-full mr-2 w-[280px] flex-col justify-between bg-background border-[0.5px] ${theme === 'dark' ? 'border-black-500' : 'border-border/60'
                      } shadow-lg rounded-xl p-4 z-50`}
                    onMouseEnter={() => setHoveredModelId(focusedModel.id)}
                  >
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 flex items-center justify-center">
                          {modelsIconViewer(focusedModel)}
                        </div>
                        <div className="text-[13px] font-semibold leading-5">
                          {providerLabel} / {focusedModel.name}
                        </div>
                        <RegionBadge region={focusedModel.region} />
                      </div>
                      <div className="text-xs leading-[18px] text-muted-foreground">
                        {description}
                      </div>
                      {meta.capabilities.length > 0 && (
                        <div className="flex flex-col gap-2">
                          <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                            {t('CAPABILITIES') || 'Capabilities'}
                          </div>
                          <div className="grid grid-cols-2 gap-1.5">
                            {meta.capabilities.map((cap) => {
                              const CapIcon = cap.icon;
                              return (
                                <div
                                  key={cap.label}
                                  className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-light-gray text-xs"
                                >
                                  <CapIcon className="h-3.5 w-3.5 text-muted-foreground" />
                                  <span>{cap.label}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    {hostingInfo && (
                      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground pt-3 border-t border-border mt-3">
                        <RegionBadge region={hostingRegion} variant={theme === 'dark' ? 'selected' : 'default'} />
                        <span>{hostingInfo}</span>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Model list */}
              <div
                ref={ensureScrollable}
                className="w-[260px] max-h-[340px] overflow-y-auto p-2"
                onMouseLeave={() => setHoveredModelId(null)}
                onScroll={(e) => {
                  const el = e.currentTarget;
                  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
                    setVisibleCount((prev) =>
                      Math.min(prev + 5, memoizedModels.length)
                    );
                  }
                }}
              >
                <div className="flex items-center gap-1 px-2 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {t('MODELS') || 'Models'}
                </div>
                {displayedModels.map((model) => {
                  const isSelected = selectedModel?.id === model.id;
                  return (
                    <DropdownMenuItem
                      key={model.id}
                      className={`p-0 rounded-md ${theme === 'dark' ? 'focus:bg-white/10' : 'focus:bg-accent/70'}`}
                      onMouseEnter={() => setHoveredModelId(model.id)}
                      onFocus={() => setHoveredModelId(model.id)}
                      onSelect={(e) => {
                        e.preventDefault();
                        handleModelSelect(model.id);
                      }}
                    >
                      <div
                        className={`flex items-center justify-between w-full px-2 py-2 cursor-pointer rounded-md transition-colors ${theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-accent/70'
                          } ${isSelected ? (theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]') : ''
                          }`}
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                            {modelsIconViewer(model)}
                          </div>
                          <div className="text-[13px] font-medium leading-5 truncate">
                            {model.name}
                          </div>
                        </div>
                        <div className="ml-2 flex items-center flex-shrink-0">
                          <RegionBadge
                            region={model.region}
                            variant={isSelected && theme === 'dark' ? 'selected' : 'default'}
                          />
                        </div>
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Tooltip content - only shows when streaming */}
        {isStreaming && <TooltipContent>
          <div className="text-xs py-1">
            {externalIsDisabled
              ? (t('MODEL_LOCKED_FOR_ASSISTANT') || 'Model selection is locked for this GPT')
              : t('CANT_CHANGE_MODEL_WHILE_STREAMING')
            }
          </div>
        </TooltipContent>}
      </Tooltip>
    </TooltipProvider>
  );
};